import { z } from 'zod';
import { IANATimezoneSchema } from '../common/timezone.schema.js';
import { AvailabilityRecurrenceTypeSchema } from './enums.schema.js';
// Discriminated union mirroring the polymorphic `caregiver_availability`
// table. ONE_OFF rows carry start_utc + end_utc; WEEKLY rows carry the
// day-of-week + local-time window + IANA timezone. The discriminator
// (`recurrenceType`) drives the validation branch.
//
// The Postgres CHECK constraint on the table backstops this — but the
// Zod schema is the source of truth for API input validation + the
// shape consumers see.
const HhMmRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
const HhMmSchema = z
    .string()
    .regex(HhMmRegex, 'Must be HH:MM in 24-hour clock (e.g. 09:00, 22:30)');
const OneOffAvailabilityFields = z
    .object({
    recurrenceType: z.literal('ONE_OFF'),
    startUtc: z.string().datetime(),
    endUtc: z.string().datetime(),
})
    .strict();
const WeeklyAvailabilityFields = z
    .object({
    recurrenceType: z.literal('WEEKLY'),
    weeklyDayOfWeek: z.number().int().min(1).max(7), // 1=Mon … 7=Sun (Luxon)
    weeklyStartLocalTime: HhMmSchema,
    weeklyEndLocalTime: HhMmSchema,
    weeklyTimezone: IANATimezoneSchema,
    weeklyEffectiveFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    weeklyEffectiveTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
})
    .strict();
export const AvailabilityCreateRequestSchema = z
    .discriminatedUnion('recurrenceType', [
    OneOffAvailabilityFields,
    WeeklyAvailabilityFields,
])
    .superRefine((val, ctx) => {
    if (val.recurrenceType === 'ONE_OFF') {
        const start = new Date(val.startUtc).getTime();
        const end = new Date(val.endUtc).getTime();
        if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'endUtc must be strictly after startUtc',
                path: ['endUtc'],
            });
        }
    }
});
export const AvailabilityResponseSchema = z.object({
    id: z.string().uuid(),
    caregiverId: z.string().uuid(),
    recurrenceType: AvailabilityRecurrenceTypeSchema,
    startUtc: z.string().datetime().nullable(),
    endUtc: z.string().datetime().nullable(),
    weeklyDayOfWeek: z.number().int().min(1).max(7).nullable(),
    weeklyStartLocalTime: z.string().nullable(),
    weeklyEndLocalTime: z.string().nullable(),
    weeklyTimezone: IANATimezoneSchema.nullable(),
    weeklyEffectiveFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    weeklyEffectiveTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const AvailabilityListResponseSchema = z.object({
    items: z.array(AvailabilityResponseSchema),
});
//# sourceMappingURL=availability.schema.js.map